import { Component, Input, OnInit } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import {
  ACTIVITY_TYPES,
  ACTIVITY_SOURCES,
  IActivity
} from '@care-portals/api-interfaces';
import { CreateActivityComponent } from '../../common/create-activity/create-activity.component';
import { DrawerStateFacade } from '../../../+store/drawer-state/drawer-state.facade';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';

type ActivitySourceFilter = 'workflow' | 'all' | 'system';

interface ActivityTypeMeta {
  label: string;
  icon?: string;
  nzIcon?: string;
  pill: string;
  dot: string;
}

@Component({
  selector: 'portals-emr-customer-activity',
  templateUrl: './customer-activity.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTimelineModule,
    CreateActivityComponent,
    NzButtonModule,
    NzRadioModule,
    NzToolTipModule,
    NzIconModule
  ]
})
export class CustomerActivityComponent implements OnInit {
  @Input() customerId: string;
  activities: IActivity[] = [];
  ACTIVITY_TYPES = ACTIVITY_TYPES;

  activitySourceFilter: ActivitySourceFilter = 'workflow';

  private readonly typeMetaMap: Record<string, ActivityTypeMeta> = {
    [ACTIVITY_TYPES.EMAIL]: {
      label: 'Email',
      icon: 'fa-regular fa-envelope',
      pill: 'bg-violet-100 text-violet-700',
      dot: '#8b5cf6'
    },
    [ACTIVITY_TYPES.CALL]: {
      label: 'Call',
      nzIcon: 'phone',
      pill: 'bg-blue-100 text-blue-600',
      dot: '#3b82f6'
    },
    [ACTIVITY_TYPES.SMS]: {
      label: 'SMS',
      icon: 'fa-regular fa-message',
      pill: 'bg-teal-100 text-teal-600',
      dot: '#14b8a6'
    },
    [ACTIVITY_TYPES.GORGIAS]: {
      label: 'Support',
      icon: 'fa-regular fa-life-ring',
      pill: 'bg-rose-100 text-rose-600',
      dot: '#ef4444'
    },
    [ACTIVITY_TYPES.OTHER]: {
      label: 'Other',
      icon: 'fa-regular fa-note-sticky',
      pill: 'bg-gray-100 text-gray-600',
      dot: '#9ca3af'
    }
  };

  private readonly fallbackTypeMeta: ActivityTypeMeta = {
    label: 'Activity',
    icon: 'fa-regular fa-circle-question',
    pill: 'bg-gray-100 text-gray-600',
    dot: '#9ca3af'
  };

  constructor(
    private readonly customerFacade: CustomerFacade,
    private readonly drawerFacade: DrawerStateFacade
  ) {
    this.customerFacade.activities$.subscribe(res => {
      this.activities = res.activities;
    });
  }

  ngOnInit(): void {
    this.customerFacade.loadCustomerActivities(this.customerId);
  }

  get displayedActivities(): IActivity[] {
    return (this.activities || []).filter(activity => {
      if (this.activitySourceFilter === 'all') return true;
      const isWorkflow = this.isWorkflowActivity(activity);
      return this.activitySourceFilter === 'workflow' ? isWorkflow : !isWorkflow;
    });
  }

  /** toEmail is an array for workflow emails and a string for system emails. */
  formatTo(toEmail: string | string[] | undefined): string {
    if (!toEmail) return '';
    return Array.isArray(toEmail) ? toEmail.join(', ') : toEmail;
  }

  openCreateActivity(type: ACTIVITY_TYPES) {
    this.customerFacade.openCreateActivity(type, this.customerId);
  }

  isWorkflowActivity(activity: IActivity): boolean {
    if (activity?.source) {
      return activity.source !== ACTIVITY_SOURCES.SYSTEM;
    }
    return (activity?.createdBy?.fullName || '')
      .toLowerCase()
      .startsWith('workflow');
  }

  activityTypeMeta(type: string): ActivityTypeMeta {
    return this.typeMetaMap[type] || this.fallbackTypeMeta;
  }
}
